import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { ShopHeader } from "@/components/ShopHeader";
import { StoreNav } from "@/components/StoreNav";
import { Trash, Package, Plus, Minus, PaperPlaneTilt, SignIn, UserPlus } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INPUT = "w-full border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary transition-colors duration-200";

const emptyAddr = { address: "", city: "", province: "", postal: "", country: "Canada" };

const AddressBlock = ({ val, on, prefix, L }) => (
  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
    <input className={`${INPUT} sm:col-span-2`} placeholder={L("Adresse", "Address")} value={val.address} onChange={(e) => on("address", e.target.value)} data-testid={`${prefix}-address`} required />
    <input className={INPUT} placeholder={L("Ville", "City")} value={val.city} onChange={(e) => on("city", e.target.value)} data-testid={`${prefix}-city`} required />
    <input className={INPUT} placeholder={L("Province", "Province")} value={val.province} onChange={(e) => on("province", e.target.value)} data-testid={`${prefix}-province`} required />
    <input className={INPUT} placeholder={L("Code postal", "Postal code")} value={val.postal} onChange={(e) => on("postal", e.target.value)} data-testid={`${prefix}-postal`} required />
    <input className={INPUT} placeholder={L("Pays", "Country")} value={val.country} onChange={(e) => on("country", e.target.value)} data-testid={`${prefix}-country`} />
  </div>
);

export default function CartPage() {
  const { t, lang } = useLang();
  const L = (fr, en) => (lang === "en" ? en : fr);
  const { items, setQty, remove, clear } = useCart();
  const navigate = useNavigate();
  const { user } = useAuth();

  // mode: null (choose) | "new" (new client) | when logged-in client -> "member"
  const [mode, setMode] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", fax: "",
    entreprise: "", succursale: "", password: "",
    coop_member: false, coop_code: "", note: "",
  });
  const [billing, setBilling] = useState({ ...emptyAddr });
  const [shipDiff, setShipDiff] = useState(false);
  const [shipping, setShipping] = useState({ ...emptyAddr });

  const isClient = user && user.role === "client";
  const isStaff = user && (user.role === "admin" || user.role === "sales_rep");

  useEffect(() => {
    if (isClient || isStaff) {
      setMode("member");
      setForm((f) => ({ ...f, email: f.email || user.email || "", succursale: f.succursale || user.name || "" }));
    }
  }, [user]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setB = (k, v) => setBilling((b) => ({ ...b, [k]: v }));
  const setS = (k, v) => setShipping((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!isClient && !isStaff) {
      if (form.password.length < 6) { toast.error(L("Mot de passe : min. 6 caractères", "Password: min 6 characters")); return; }
      if (form.coop_member && !/^\d{8}$/.test(form.coop_code.trim())) { toast.error(L("Le code coop doit contenir 8 chiffres", "Coop code must be 8 digits")); return; }
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/shop/checkout`, {
        first_name: form.first_name, last_name: form.last_name, email: form.email,
        phone: form.phone, fax: form.fax, entreprise: form.entreprise, succursale: form.succursale,
        note: form.note,
        coop_member: form.coop_member, coop_code: form.coop_code, password: form.password,
        billing, shipping_different: shipDiff, shipping: shipDiff ? shipping : null,
        items: items.map((x) => ({ product_id: x.product_id, product_code: x.product_code, name: x.name[lang] || x.name.fr, qty: x.qty })),
      });
      clear();
      if (res.data.account_created) toast.success(L("Compte créé et commande envoyée !", "Account created and order sent!"));
      else toast.success(t.orderPlaced || L("Commande envoyée !", "Order sent!"));
      // Rechargement complet pour que l'auth prenne en compte le nouveau cookie de session
      window.location.assign(isStaff ? "/admin/orders" : "/portal");
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        toast.error(L("Ce courriel a déjà un compte. Connectez-vous.", "This email already has an account. Please log in."));
        navigate("/login");
      } else {
        const d = err.response?.data?.detail;
        toast.error(typeof d === "string" ? d : (t.orderFailed || L("Échec de la commande", "Order failed")));
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <ShopHeader />
      <div className="mx-auto max-w-[1280px] px-4 py-4"><StoreNav active="cart" /></div>
      <main className="px-6 py-8">
        <h1 className="mb-2 font-display text-4xl font-black tracking-tighter">{t.cart}</h1>
        <p className="mb-6 text-sm text-muted-foreground">{t.priceHidden || L("Les prix vous seront confirmés par notre équipe.", "Prices will be confirmed by our team.")}</p>

        {items.length === 0 ? (
          <div className="border border-border bg-card p-12 text-center">
            <p className="text-sm text-muted-foreground">{t.emptyCart}</p>
            <button onClick={() => navigate("/")} className="mt-4 bg-primary px-4 py-2 text-xs font-bold uppercase tracking-wider text-white" data-testid="continue-shopping">{t.continueShopping}</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 border border-border bg-card divide-y divide-border" data-testid="cart-items">
              {items.map((x) => (
                <div key={x.product_id} className="flex items-center gap-4 p-4">
                  <div className="h-16 w-16 shrink-0 bg-muted">
                    {x.image ? <img loading="lazy" decoding="async" src={x.image} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><Package size={22} /></div>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[11px] text-primary">{x.product_code}</div>
                    <div className="truncate text-sm font-medium">{x.name[lang] || x.name.fr}</div>
                  </div>
                  <div className="flex items-center border border-border">
                    <button onClick={() => setQty(x.product_id, x.qty - 1)} className="px-2 py-1.5 hover:bg-muted"><Minus size={12} weight="bold" /></button>
                    <span className="w-10 text-center text-sm">{x.qty}</span>
                    <button onClick={() => setQty(x.product_id, x.qty + 1)} className="px-2 py-1.5 hover:bg-muted"><Plus size={12} weight="bold" /></button>
                  </div>
                  <button onClick={() => remove(x.product_id)} className="text-muted-foreground hover:text-primary transition-colors duration-200"><Trash size={18} /></button>
                </div>
              ))}
            </div>

            <div className="border border-border bg-card p-6">
              {/* Choix connexion / nouveau client */}
              {!isClient && !isStaff && mode === null && (
                <div className="space-y-3" data-testid="checkout-choice">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{L("Finaliser la commande", "Complete your order")}</div>
                  <button onClick={() => navigate("/login")} data-testid="choice-login"
                    className="flex w-full items-center justify-center gap-2 bg-primary py-3 text-sm font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5">
                    <SignIn size={18} weight="bold" /> {L("J'ai déjà un compte — Me connecter", "I have an account — Log in")}
                  </button>
                  <button onClick={() => setMode("new")} data-testid="choice-new"
                    className="flex w-full items-center justify-center gap-2 border border-primary py-3 text-sm font-bold uppercase tracking-wider text-primary hover:bg-primary hover:text-white transition-colors duration-200">
                    <UserPlus size={18} weight="bold" /> {L("Nouveau client — Continuer", "New customer — Continue")}
                  </button>
                </div>
              )}

              {(mode === "new" || mode === "member") && (
                <form onSubmit={submit} className="space-y-3" data-testid="checkout-form">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    {mode === "new" ? L("Nouveau client", "New customer") : L("Vos informations", "Your information")}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <input className={INPUT} placeholder={L("Prénom", "First name")} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="checkout-first_name" required />
                    <input className={INPUT} placeholder={L("Nom", "Last name")} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="checkout-last_name" required />
                  </div>
                  <input className={INPUT} placeholder={L("Courriel", "Email")} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="checkout-email" required disabled={isClient} />
                  <div className="grid grid-cols-2 gap-3">
                    <input className={INPUT} placeholder={L("Téléphone", "Phone")} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="checkout-phone" />
                    <input className={INPUT} placeholder={L("Télécopieur", "Fax")} value={form.fax} onChange={(e) => set("fax", e.target.value)} data-testid="checkout-fax" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input className={INPUT} placeholder={L("Entreprise", "Enterprise")} value={form.entreprise} onChange={(e) => set("entreprise", e.target.value)} data-testid="checkout-entreprise" />
                    <input className={INPUT} placeholder={L("Succursale", "Branch")} value={form.succursale} onChange={(e) => set("succursale", e.target.value)} data-testid="checkout-succursale" />
                  </div>

                  {mode === "new" && (
                    <>
                      <input className={INPUT} type="password" placeholder={L("Mot de passe (min. 6)", "Password (min 6)")} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="checkout-password" required />
                      <div className="rounded border border-border p-3">
                        <div className="mb-2 text-xs font-semibold">{L("Membre de la coop ?", "Co-op member?")}</div>
                        <div className="flex gap-4 text-sm">
                          <label className="flex items-center gap-1.5"><input type="radio" name="coop" checked={form.coop_member === true} onChange={() => set("coop_member", true)} data-testid="coop-yes" /> {L("Oui", "Yes")}</label>
                          <label className="flex items-center gap-1.5"><input type="radio" name="coop" checked={form.coop_member === false} onChange={() => set("coop_member", false)} data-testid="coop-no" /> {L("Non", "No")}</label>
                        </div>
                        {form.coop_member && (
                          <input className={`${INPUT} mt-2`} placeholder={L("Code coop (8 chiffres)", "Co-op code (8 digits)")} value={form.coop_code} onChange={(e) => set("coop_code", e.target.value)} data-testid="checkout-coop_code" inputMode="numeric" maxLength={8} required />
                        )}
                      </div>
                    </>
                  )}

                  <div className="pt-1 text-xs font-semibold text-muted-foreground">{L("Adresse de facturation", "Billing address")}</div>
                  <AddressBlock val={billing} on={setB} prefix="billing" L={L} />

                  <label className="flex items-center gap-2 pt-1 text-sm">
                    <input type="checkbox" checked={shipDiff} onChange={(e) => setShipDiff(e.target.checked)} data-testid="shipping-different" />
                    {L("Adresse de livraison différente", "Different shipping address")}
                  </label>
                  {shipDiff && (<><div className="text-xs font-semibold text-muted-foreground">{L("Adresse de livraison", "Shipping address")}</div><AddressBlock val={shipping} on={setS} prefix="shipping" L={L} /></>)}

                  <textarea className={INPUT} rows={3} placeholder={t.note || "Note"} value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="checkout-note" />

                  <button type="submit" disabled={submitting} data-testid="place-order-button"
                    className="flex w-full items-center justify-center gap-2 bg-primary py-3 text-sm font-bold uppercase tracking-wider text-white transition-transform duration-200 ease-out hover:-translate-y-0.5 disabled:opacity-60">
                    <PaperPlaneTilt size={18} weight="bold" /> {submitting ? t.loading : (t.placeOrder || L("Envoyer la commande", "Send order"))}
                  </button>
                  {mode === "new" && (
                    <button type="button" onClick={() => setMode(null)} className="w-full text-center text-xs text-muted-foreground hover:text-primary">{L("← Retour", "← Back")}</button>
                  )}
                </form>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
